import React, { useEffect, useRef } from 'react';
import 'twin.macro';
import { RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import { CSSInterpolation } from '@emotion/serialize';
import { Color } from 'types/colors';
import _sample from 'lodash-es/sample';

export const mediumColors = [
  { name: 'white', bg: tw`bg-white`, border: tw`border-gray-200` },
  { name: 'red-200', bg: tw`bg-red-200`, border: tw`border-red-300` },
  { name: 'orange-200', bg: tw`bg-orange-200`, border: tw`border-orange-300` },
  { name: 'yellow-200', bg: tw`bg-yellow-200`, border: tw`border-yellow-300` },
  { name: 'green-200', bg: tw`bg-green-200`, border: tw`border-green-300` },
  { name: 'blue-200', bg: tw`bg-blue-200`, border: tw`border-blue-300` },
  { name: 'indigo-200', bg: tw`bg-indigo-200`, border: tw`border-indigo-300` },
  { name: 'purple-200', bg: tw`bg-purple-200`, border: tw`border-purple-300` },
  { name: 'pink-200', bg: tw`bg-pink-200`, border: tw`border-pink-300` },
  { name: 'gray-200', bg: tw`bg-gray-200`, border: tw`border-gray-300` },
  { name: 'gray-900', bg: tw`bg-gray-900`, border: tw`border-gray-800` },
];

export const lightColors = [
  { name: 'white', bg: tw`bg-white`, border: tw`border-gray-200` },
  { name: 'red-100', bg: tw`bg-red-100`, border: tw`border-red-200` },
  { name: 'orange-100', bg: tw`bg-orange-100`, border: tw`border-orange-200` },
  { name: 'yellow-100', bg: tw`bg-yellow-100`, border: tw`border-yellow-200` },
  { name: 'green-100', bg: tw`bg-green-100`, border: tw`border-green-200` },
  { name: 'blue-100', bg: tw`bg-blue-100`, border: tw`border-blue-200` },
  { name: 'indigo-100', bg: tw`bg-indigo-100`, border: tw`border-indigo-200` },
  { name: 'purple-100', bg: tw`bg-purple-100`, border: tw`border-purple-200` },
  { name: 'pink-100', bg: tw`bg-pink-100`, border: tw`border-pink-200` },
  { name: 'gray-100', bg: tw`bg-gray-100`, border: tw`border-gray-200` },
  { name: 'gray-900', bg: tw`bg-gray-900`, border: tw`border-gray-800` },
];

export function randomLightColor() {
  return _sample(lightColors.map(l => l.name)) as Color;
}

export function randomMediumColor() {
  return _sample(mediumColors.map(l => l.name)) as Color;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  light?: boolean;
}

function ColorOption({
  colorName,
  colorBg,
  colorBorder,
}: {
  colorName: string;
  colorBg: CSSInterpolation;
  colorBorder: CSSInterpolation;
}) {
  return (
    <RadioGroup.Option
      value={colorName}
      tw="flex cursor-pointer items-center h-8 w-8 rounded-full focus:outline-none"
    >
      {({ checked }) => (
        <>
          <span
            tw="h-8 w-8 rounded-full"
            css={[tw`h-8 w-8 rounded-full border`, colorBg, colorBorder]}
          />
          {checked && (
            <div tw="absolute inset-0 flex items-center justify-center">
              <CheckIcon tw="h-4 w-4" />
            </div>
          )}
        </>
      )}
    </RadioGroup.Option>
  );
}

export function ColorSelector(props: Props) {
  const { value, onChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && selectedRef.current) {
      const container = containerRef.current;
      const selectedElement = selectedRef.current;

      // Calcul de la position de scroll
      const containerWidth = container.offsetWidth;
      const selectedLeft = selectedElement.offsetLeft;
      const selectedWidth = selectedElement.offsetWidth;

      // Centre l'élément sélectionné
      const scrollPosition =
        selectedLeft - containerWidth / 2 + selectedWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: 'instant',
      });
    }
  }, []);

  const colors = props.light ? lightColors : mediumColors;

  return (
    <RadioGroup value={value} onChange={onChange} tw="relative">
      <div
        ref={containerRef}
        tw="flex items-center space-x-4 py-2 overflow-x-auto relative"
      >
        {colors.map(color => (
          <div
            key={color.name}
            ref={color.name === value ? selectedRef : null}
            tw="relative flex-shrink-0"
          >
            <ColorOption
              colorName={color.name}
              colorBg={color.bg}
              colorBorder={color.border}
            />
          </div>
        ))}
      </div>
    </RadioGroup>
  );
}
